import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Fingerprint,
  PenLine,
  Upload,
  Save,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Info,
  CalendarCheck,
  Clock,
  CalendarOff,
  LayoutList,
  Settings2,
  Edit3,
} from "lucide-react";

import API from "../../services/api";
import {
  fetchWfSetupOptions,
  fetchWfAttendanceProfile,
  fetchWfHolidays,
  updateAttendanceConfiguration,
  updateWorkScheduleConfiguration,
} from "../../Moduels/attendance/wfApi";
import { readWorkSchedule } from "../pages/Attendance/attendanceConfigurationMeta";
import { navigateToSettingsHome } from "./settingsExit";
import { useToast } from "../../Moduels/Context/ToastContext";
import {
  fetchShiftTemplates,
  createShiftTemplate,
  updateShiftTemplate,
  deleteShiftTemplate,
} from "../../Moduels/attendance/shiftManagementApi";
import ShiftFormModal from "../shift/ShiftFormModal";
import { resolveCurrentSource } from "../pages/Attendance/attendanceConfigurationMeta";
import "./AttendanceLeaveSetup.css";
import WeeklyMonthlyOffTab from "./WeeklyMonthlyOffTab";
import {
  monthlyOffRulesFromAttendance,
  weekOffWeekdaysFromAttendance,
} from "../../Moduels/attendance/attendanceCalendarUtils";

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtErr(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((i) => i?.msg || String(i)).join("; ");
  return fallback;
}

function fmt12(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${String(hr).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")} ${ampm}`;
}

// ─── constants ───────────────────────────────────────────────────────────────

/** Work schedule modes when shift management is active (capture method stays separate). */
const SHIFT_PIPELINE_OPTIONS = [
  {
    code: "weekly_shift_pattern",
    label: "Employee shift assignments",
    description:
      "Resolve attendance from shifts in step 2.2 and employee allocations (Attendance → Shift Management). Applies expected in/out, grace, late, and early rules.",
  },
  {
    code: "shift_roster",
    label: "Shift roster board",
    description:
      "Use published roster plans when available, then employee assignments and org defaults. Best when HR publishes daily/weekly rosters.",
  },
];

const METHOD_OPTIONS = [
  {
    code: "default_present",
    label: "Default Present",
    description: "Mark all employees as present by default for each working day.",
    icon: CheckCircle2,
  },
  {
    code: "manual_hr",
    label: "Manual Attendance",
    description: "Mark attendance manually for employees on a daily basis.",
    icon: PenLine,
  },
  {
    code: "excel_upload",
    label: "Excel Upload",
    description: "Upload attendance data using an Excel file.",
    icon: Upload,
  },
  {
    code: "biometric",
    label: "Biometric",
    description: "Use biometric devices to capture employee attendance.",
    icon: Fingerprint,
  },
];

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const OVERTIME_OPTIONS = [
  { value: "after_full_day", label: "After Full Day" },
  { value: "fixed_hours", label: "Fixed Hours" },
  { value: "daily_threshold", label: "Daily Threshold" },
];

const SETUP_TAB_KEYS = ["method", "shifts", "rules", "weekly-off"];

const STEP_DONE_COPY = {
  method: {
    title: "2.1 Attendance Method",
    body: "This page is completed. Capture method and shift management policy are configured. Click Edit to change biometric/manual capture or activate shift management for the attendance pipeline.",
  },
  shifts: {
    title: "2.2 Shift Management",
    body: "This page is completed. Shifts are already configured. If you want to add or change shifts, click Edit.",
  },
  rules: {
    title: "2.3 Attendance Rules",
    body: "This page is completed. Attendance rules are already configured. If you want to edit this page, click Edit.",
  },
  "weekly-off": {
    title: "2.4 Weekly Off & Monthly Off",
    body: "This page is completed. Weekly and monthly off days are already configured. If you want to edit this page, click Edit.",
  },
};

function deriveAlsFlags({ source, shiftCount, att = {} }) {
  return {
    method: Boolean(source),
    shifts: Number(shiftCount) > 0,
    rules: Boolean(att.overtime_rule),
    "weekly-off": Boolean(att.week_off_option) || Array.isArray(att.monthly_off_rules),
  };
}

function isAlsSetupComplete(flags = {}) {
  return SETUP_TAB_KEYS.every((k) => flags[k]);
}

function StepCompletedPanel({ tabKey, currentValue, onEdit, onViewSummary, showSummaryLink }) {
  const copy = STEP_DONE_COPY[tabKey] || {};
  return (
    <div className="als-tab-body">
      <div className="als-done-panel">
        <div className="als-done-icon">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="als-done-title">This page is completed</h3>
        {copy.title ? <p className="als-done-step">{copy.title}</p> : null}
        <p className="als-done-body">{copy.body}</p>
        {currentValue ? (
          <div className="als-done-current">
            <span className="als-done-current-label">Current setting</span>
            <span className="als-done-current-value">{currentValue}</span>
          </div>
        ) : null}
        <div className="als-done-actions">
          <button type="button" className="als-btn-primary" onClick={onEdit}>
            <Edit3 size={14} />
            Edit this page
          </button>
          {showSummaryLink ? (
            <button type="button" className="als-btn-ghost" onClick={onViewSummary}>
              View configuration summary
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Tab 1 – Attendance Method ───────────────────────────────────────────────

function AttendanceMethodTab({ onConfigured }) {
  const toast = useToast();
  const [selected, setSelected] = useState("");
  const [saved, setSaved] = useState("");
  const [shiftPipelineOn, setShiftPipelineOn] = useState(false);
  const [savedShiftPipelineOn, setSavedShiftPipelineOn] = useState(false);
  const [shiftMode, setShiftMode] = useState("weekly_shift_pattern");
  const [savedShiftMode, setSavedShiftMode] = useState("weekly_shift_pattern");
  const [savedRosterConfig, setSavedRosterConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [, profile, hrRes] = await Promise.all([
          fetchWfSetupOptions(),
          fetchWfAttendanceProfile(),
          API.get("/organisation/me/hr-settings"),
        ]);
        const hrAtt = hrRes?.data?.attendance_settings || hrRes?.data?.hr_settings?.attendance || {};
        const source = resolveCurrentSource({}, hrAtt, profile);
        setSelected(source || "manual_hr");
        setSaved(source || "");

        const ws = readWorkSchedule(profile || {}, hrAtt);
        const featuresOn = Boolean(
          profile?.shift_features_enabled ??
            (ws.enable_shift_roster ||
              ["shift_roster", "rotational_shift", "weekly_shift_pattern", "open_shift"].includes(
                ws.work_schedule_type
              ))
        );
        const mode =
          ws.work_schedule_type && ws.work_schedule_type !== "fixed_schedule"
            ? ws.work_schedule_type
            : "weekly_shift_pattern";
        setShiftPipelineOn(featuresOn);
        setSavedShiftPipelineOn(featuresOn);
        setShiftMode(mode);
        setSavedShiftMode(mode);
        setSavedRosterConfig(Array.isArray(ws.roster_config) ? ws.roster_config : []);
      } catch (e) {
        toast.error(fmtErr(e, "Failed to load attendance method"));
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const captureDirty = selected !== saved;
  const shiftDirty =
    shiftPipelineOn !== savedShiftPipelineOn ||
    (shiftPipelineOn && shiftMode !== savedShiftMode);
  const canSave = selected && (captureDirty || shiftDirty);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (captureDirty) {
        await updateAttendanceConfiguration(selected);
        setSaved(selected);
      }

      if (shiftDirty) {
        if (shiftPipelineOn) {
          await updateWorkScheduleConfiguration({
            work_schedule_type: shiftMode,
            enable_shift_roster: true,
            roster_config:
              savedRosterConfig.length > 0
                ? savedRosterConfig
                : ["weekly_roster", "monthly_roster"],
          });
        } else {
          await updateWorkScheduleConfiguration({
            work_schedule_type: "fixed_schedule",
            enable_shift_roster: false,
            roster_config: savedRosterConfig,
          });
        }
        setSavedShiftPipelineOn(shiftPipelineOn);
        setSavedShiftMode(shiftMode);
      }

      window.dispatchEvent(new Event("attendance-profile-changed"));
      toast.success(
        shiftDirty && shiftPipelineOn
          ? "Attendance method and shift management policy saved. The attendance engine will use shift rules."
          : "Attendance method saved successfully."
      );
      await onConfigured?.();
    } catch (e) {
      toast.error(fmtErr(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="als-loading">
        <Loader2 className="als-spin" size={22} />
        Loading…
      </div>
    );
  }

  return (
    <div className="als-tab-body">
      <div className="als-section-header">
        <div className="als-section-icon">
          <CheckCircle2 size={18} />
        </div>
        <div>
          <h2 className="als-section-title">2.1 Attendance Method</h2>
          <p className="als-section-sub">Define the source or method used to capture employee attendance.</p>
        </div>
      </div>

      <div className="als-method-grid">
        {METHOD_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isSelected = selected === opt.code;
          return (
            <label key={opt.code} className={`als-method-card ${isSelected ? "als-method-card--selected" : ""}`}>
              <input
                type="radio"
                name="attendance_method"
                value={opt.code}
                checked={isSelected}
                onChange={() => setSelected(opt.code)}
                className="als-radio"
              />
              <div className="als-method-card-inner">
                <div className={`als-method-icon ${isSelected ? "als-method-icon--active" : ""}`}>
                  <Icon size={18} />
                </div>
                <div>
                  <div className="als-method-label">{opt.label}</div>
                  <div className="als-method-desc">{opt.description}</div>
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="als-info-bar">
        <Info size={14} />
        The selected attendance method will be applied for all employees.
      </div>

      <div className="als-shift-section">
        <div className="als-section-header als-shift-section-header">
          <div className="als-section-icon als-section-icon--amber">
            <Clock size={18} />
          </div>
          <div>
            <h2 className="als-section-title">Shift management policy</h2>
            <p className="als-section-sub">
              Turn on to run the full attendance pipeline with shift templates, allocations, grace, late,
              and early-out rules. Your capture method above (e.g. Biometric) stays the same.
            </p>
          </div>
        </div>

        <div className="als-shift-toggle-row">
          <button
            type="button"
            className={`als-shift-toggle-btn ${shiftPipelineOn ? "als-shift-toggle-btn--on" : ""}`}
            onClick={() => setShiftPipelineOn(true)}
          >
            Activate shift management
          </button>
          <button
            type="button"
            className={`als-shift-toggle-btn ${!shiftPipelineOn ? "als-shift-toggle-btn--off" : ""}`}
            onClick={() => setShiftPipelineOn(false)}
          >
            Use fixed schedule only
          </button>
        </div>

        {shiftPipelineOn ? (
          <>
            <div className="als-method-grid als-shift-mode-grid">
              {SHIFT_PIPELINE_OPTIONS.map((opt) => {
                const isSelected = shiftMode === opt.code;
                return (
                  <label
                    key={opt.code}
                    className={`als-method-card ${isSelected ? "als-method-card--selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="shift_pipeline_mode"
                      value={opt.code}
                      checked={isSelected}
                      onChange={() => setShiftMode(opt.code)}
                      className="als-radio"
                    />
                    <div className="als-method-card-inner">
                      <div>
                        <div className="als-method-label">{opt.label}</div>
                        <div className="als-method-desc">{opt.description}</div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="als-info-bar als-info-bar--success">
              <Info size={14} />
              Shift management is <strong>on</strong>. Define shifts in{" "}
              <Link to="/settings/attendance-leave-setup?tab=shifts">step 2.2</Link>, assign employees under{" "}
              <Link to="/attendance/shifts">Attendance → Shift Management</Link>, then recompute or sync punches.
            </div>
          </>
        ) : (
          <div className="als-info-bar">
            <Info size={14} />
            Shift management is <strong>off</strong>. Attendance ignores employee shift allocations at runtime;
            org calendar and fixed rules apply instead.
          </div>
        )}
      </div>

      <div className="als-footer">
        <button
          type="button"
          className="als-btn-primary"
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? <Loader2 size={14} className="als-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
}

// ─── Tab 2 – Shift Management ────────────────────────────────────────────────

function ShiftManagementTab({ onConfigured, returnTo, autoCreate }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, template: null });
  const PAGE_SIZE = 10;

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      try {
        const data = await fetchShiftTemplates({ page: p, page_size: PAGE_SIZE });
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        toast.error(fmtErr(e, "Failed to load shifts"));
      } finally {
        setLoading(false);
      }
    },
    [page, toast]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (autoCreate) {
      setModal({ open: true, template: null });
    }
  }, [autoCreate]);

  const handleSave = async (payload) => {
    try {
      if (modal.template?.template_id) {
        await updateShiftTemplate(modal.template.template_id, payload);
        toast.success("Shift updated.");
      } else {
        await createShiftTemplate(payload);
        toast.success("Shift created.");
      }
      setModal({ open: false, template: null });
      load(1);
      setPage(1);
      await onConfigured?.();
      if (returnTo) {
        navigate(returnTo);
      }
    } catch (e) {
      throw e;
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete shift "${row.name}"?`)) return;
    try {
      await deleteShiftTemplate(row.template_id);
      toast.success("Shift deleted.");
      load();
      await onConfigured?.();
    } catch (e) {
      toast.error(fmtErr(e, "Failed to delete"));
    }
  };

  const STATUS_CYCLE = ["active", "draft", "inactive"];

  function resolveStatus(row) {
    if (row.status) return row.status;
    return row.is_active ? "active" : "inactive";
  }

  const handleCycleStatus = async (row) => {
    const current = resolveStatus(row);
    const nextStatus = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
    try {
      await updateShiftTemplate(row.template_id, {
        is_active: nextStatus === "active",
        status: nextStatus,
      });
      toast.success(`Shift "${row.name}" status changed to ${nextStatus}.`);
      setItems((prev) =>
        prev.map((r) =>
          r.template_id === row.template_id
            ? { ...r, is_active: nextStatus === "active", status: nextStatus }
            : r
        )
      );
    } catch (e) {
      toast.error(fmtErr(e, "Failed to update status"));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="als-tab-body">
      <div className="als-section-header">
        <div className="als-section-icon">
          <CheckCircle2 size={18} />
        </div>
        <div className="als-section-header-text">
          <h2 className="als-section-title">2.2 Shift Management</h2>
          <p className="als-section-sub">Define and manage work shifts (shift master). Employee allocation is under Attendance → Shift Management.</p>
        </div>
        <div className="als-ml-auto flex flex-wrap gap-2">
          <Link className="als-btn-secondary" to="/attendance/shifts">
            Assign employees
          </Link>
          <button
            type="button"
            className="als-btn-primary"
            onClick={() => setModal({ open: true, template: null })}
          >
            <Plus size={14} />
            Add Shift
          </button>
        </div>
      </div>

      <div className="als-status-hint">
        <Info size={13} />
        Click on the <strong>Status</strong> pill to cycle: <span className="als-hint-active">Active</span> → <span className="als-hint-draft">Draft</span> → <span className="als-hint-inactive">Inactive</span>
      </div>

      <div className="als-table-wrap">
        <table className="als-table">
          <thead>
            <tr>
              <th>Shift Code</th>
              <th>Shift Name</th>
              <th>In Time</th>
              <th>Out Time</th>
              <th>Break Duration</th>
              <th>Grace Minutes</th>
              <th>Status</th>
              <th>Employees Assigned</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="als-table-empty">
                  <Loader2 size={18} className="als-spin" /> Loading shifts…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} className="als-table-empty">
                  No shifts configured yet. Click "Add Shift" to create one.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.template_id}>
                  <td className="als-td-code">{row.code}</td>
                  <td className="als-td-name">{row.name}</td>
                  <td>{fmt12(row.start_time)}</td>
                  <td>{fmt12(row.end_time)}</td>
                  <td>
                    {row.break_minutes
                      ? `${String(Math.floor(row.break_minutes / 60)).padStart(2, "0")}:${String(row.break_minutes % 60).padStart(2, "0")}`
                      : "—"}
                  </td>
                  <td>{row.config_json?.grace_minutes ?? 15}</td>
                  <td>
                    <button
                      type="button"
                      className={`als-status-pill als-status-pill--btn ${
                        resolveStatus(row) === "active"
                          ? "als-status-pill--active"
                          : resolveStatus(row) === "draft"
                          ? "als-status-pill--draft"
                          : "als-status-pill--inactive"
                      }`}
                      title={`Current: ${resolveStatus(row)} — click to change`}
                      onClick={() => handleCycleStatus(row)}
                    >
                      {resolveStatus(row).charAt(0).toUpperCase() + resolveStatus(row).slice(1)}
                    </button>
                  </td>
                  <td className="als-td-num">{row.employees_assigned ?? 0}</td>
                  <td>
                    <div className="als-action-btns">
                      <Link
                        className="als-link-btn"
                        title="View employees on this shift"
                        to={`/attendance/shifts?shift=${row.template_id}`}
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        className="als-icon-btn als-icon-btn--edit"
                        title="Edit"
                        onClick={() => setModal({ open: true, template: row })}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        className="als-icon-btn als-icon-btn--delete"
                        title="Delete"
                        onClick={() => handleDelete(row)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="als-pagination">
          <span className="als-pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)} to {Math.min(page * PAGE_SIZE, total)} of {total} entries
          </span>
          <div className="als-pagination-controls">
            <select
              className="als-select-sm"
              value={PAGE_SIZE}
              onChange={() => {}}
            >
              <option value={10}>10 per page</option>
            </select>
            <button
              type="button"
              className="als-page-btn"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); load(page - 1); }}
            >
              ‹
            </button>
            <span className="als-page-num">{page}</span>
            <button
              type="button"
              className="als-page-btn"
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); load(page + 1); }}
            >
              ›
            </button>
          </div>
        </div>
      )}

      <ShiftFormModal
        open={modal.open}
        onClose={() => setModal({ open: false, template: null })}
        template={modal.template}
        readOnly={false}
        onSave={handleSave}
      />
    </div>
  );
}

// ─── Tab 3 – Attendance Rules ────────────────────────────────────────────────

function AttendanceRulesTab({ onConfigured }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_day_hours: "8.00",
    half_day_hours: "4.00",
    late_coming_threshold_minutes: "15",
    early_exit_threshold_minutes: "15",
    overtime_rule: "after_full_day",
  });
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get("/organisation/me/hr-settings");
        const att = res.data?.attendance_settings || res.data?.hr_settings?.attendance || {};
        const vals = {
          full_day_hours: String(att.full_day_hours ?? "8.00"),
          half_day_hours: String(att.half_day_hours ?? "4.00"),
          late_coming_threshold_minutes: String(att.late_coming_threshold_minutes ?? "15"),
          early_exit_threshold_minutes: String(att.early_exit_threshold_minutes ?? "15"),
          overtime_rule: att.overtime_rule || "after_full_day",
        };
        setForm(vals);
        setSaved(att.overtime_rule != null ? vals : null);
      } catch (e) {
        toast.error(fmtErr(e, "Failed to load attendance rules"));
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isDirty = !saved || JSON.stringify(form) !== JSON.stringify(saved);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.patch("/organisation/me/hr-settings", {
        attendance: {
          full_day_hours: parseFloat(form.full_day_hours) || 8,
          half_day_hours: parseFloat(form.half_day_hours) || 4,
          late_coming_threshold_minutes: parseInt(form.late_coming_threshold_minutes, 10) || 15,
          early_exit_threshold_minutes: parseInt(form.early_exit_threshold_minutes, 10) || 15,
          overtime_rule: form.overtime_rule,
        },
      });
      setSaved({ ...form });
      toast.success("Attendance rules saved. Calendar marks and overtime now use these hours.");
      await onConfigured?.();
    } catch (e) {
      toast.error(fmtErr(e, "Failed to save rules"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="als-loading">
        <Loader2 size={22} className="als-spin" />
        Loading…
      </div>
    );
  }

  const fields = [
    {
      key: "full_day_hours",
      label: "Full Day Hours",
      hint: "Minimum working hours required to consider a full day.",
      type: "number",
      step: "0.5",
    },
    {
      key: "half_day_hours",
      label: "Half Day Hours",
      hint: "Minimum working hours required to consider a half day.",
      type: "number",
      step: "0.5",
    },
    {
      key: "late_coming_threshold_minutes",
      label: "Late Coming Threshold (Minutes)",
      hint: "Employees coming after this time will be marked as late.",
      type: "number",
    },
    {
      key: "early_exit_threshold_minutes",
      label: "Early Exit Threshold (Minutes)",
      hint: "Employees leaving before this time will be marked as early exit.",
      type: "number",
    },
  ];

  return (
    <div className="als-tab-body">
      <div className="als-section-header">
        <div className="als-section-icon">
          <CheckCircle2 size={18} />
        </div>
        <div>
          <h2 className="als-section-title">2.3 Attendance Rules</h2>
          <p className="als-section-sub">Configure rules for attendance calculations.</p>
        </div>
      </div>

      <div className="als-rules-form">
        {fields.map((f) => (
          <div key={f.key} className="als-rules-row">
            <label className="als-rules-label" htmlFor={`rule-${f.key}`}>
              {f.label} <span className="als-required">*</span>
            </label>
            <input
              id={`rule-${f.key}`}
              type={f.type}
              step={f.step}
              min="0"
              className="als-input"
              value={form[f.key]}
              onChange={(e) => setField(f.key, e.target.value)}
            />
            <span className="als-rules-hint">{f.hint}</span>
          </div>
        ))}

        <div className="als-rules-row">
          <label className="als-rules-label" htmlFor="rule-overtime">
            Overtime Rule <span className="als-required">*</span>
          </label>
          <select
            id="rule-overtime"
            className="als-input als-select"
            value={form.overtime_rule}
            onChange={(e) => setField("overtime_rule", e.target.value)}
          >
            {OVERTIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="als-rules-hint">Overtime will be calculated after completing full day hours.</span>
        </div>
      </div>

      <div className="als-footer">
        <button
          type="button"
          className="als-btn-primary"
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? <Loader2 size={14} className="als-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save Rules"}
        </button>
      </div>
    </div>
  );
}

// ─── Tab 4 – Weekly Off & Monthly Off ─────────────────────────────────────────
// Calendar-first step lives in ./WeeklyMonthlyOffTab (replaces the former 2.4 / 2.5 tabs).

function weeklyOffSummary(att = {}) {
  let days = weekOffWeekdaysFromAttendance(att);
  const hasRuleConfig = Array.isArray(att.monthly_off_rules) || att.monthly_off_enabled;
  if (att.alternate_saturday && !hasRuleConfig) days = days.filter((d) => d !== 5);
  const weekly = [...days].sort().map((i) => WEEKDAYS[i]);
  const monthly = monthlyOffRulesFromAttendance(att).map(
    (r) => `${r.occurrence} ${WEEKDAYS[WEEKDAY_KEYS.indexOf(r.weekday)]}`
  );
  return { weekly, monthly };
}

// ─── Tab 6 – Configuration Summary ──────────────────────────────────────────

const METHOD_LABEL_MAP = {
  default_present: "Default Present",
  manual_hr: "Manual Attendance",
  excel_upload: "Excel Upload",
  biometric: "Biometric Device",
  browser_login: "Website Login",
};

const OT_RULE_LABEL = {
  after_full_day: "After Full Day",
  fixed_hours: "Fixed Hours",
  daily_threshold: "Daily Threshold",
};

async function loadAlsStatus() {
  const [hrRes, shiftsRes, profile] = await Promise.all([
    API.get("/organisation/me/hr-settings").catch(() => null),
    fetchShiftTemplates({ page: 1, page_size: 1 }).catch(() => ({ items: [], total: 0 })),
    fetchWfAttendanceProfile().catch(() => null),
  ]);
  const hr = hrRes?.data || {};
  const att = hr.attendance_settings || hr.hr_settings?.attendance || {};
  const source = resolveCurrentSource({}, att, profile || {});
  const shiftCount = Number(shiftsRes?.total) || 0;
  return {
    att,
    source,
    shiftCount,
    shiftFeaturesEnabled: Boolean(profile?.shift_features_enabled),
    workScheduleType: profile?.work_schedule_type,
    flags: deriveAlsFlags({ source, shiftCount, att }),
  };
}

const SHIFT_MODE_LABEL = {
  weekly_shift_pattern: "Employee assignments",
  shift_roster: "Roster board",
  rotational_shift: "Rotational shift",
  open_shift: "Open shift",
  fixed_schedule: "Fixed schedule",
};

function formatAlsCurrentValue(key, status = {}) {
  const { source, shiftCount, att = {}, shiftFeaturesEnabled, workScheduleType } = status;
  if (key === "method") {
    const capture = METHOD_LABEL_MAP[source] || source || "Configured";
    if (shiftFeaturesEnabled) {
      const mode = SHIFT_MODE_LABEL[workScheduleType] || workScheduleType || "Shift policy";
      return `${capture} · Shift management on (${mode})`;
    }
    return `${capture} · Shift management off`;
  }
  if (key === "shifts") {
    const n = Number(shiftCount) || 0;
    return n === 1 ? "1 shift configured" : `${n} shifts configured`;
  }
  if (key === "rules") {
    const full = att.full_day_hours ?? "8";
    const ot = OT_RULE_LABEL[att.overtime_rule] || att.overtime_rule;
    return ot ? `Full day ${full} hrs · Overtime: ${ot}` : `Full day ${full} hrs`;
  }
  if (key === "weekly-off") {
    const { weekly, monthly } = weeklyOffSummary(att);
    const w = weekly.length ? `Weekly: ${weekly.join(", ")}` : "No weekly off";
    return monthly.length ? `${w} · Monthly: ${monthly.join(", ")}` : w;
  }
  return "";
}

function SummaryItem({ text }) {
  return (
    <div className="als-sum-item">
      <CheckCircle2 size={14} className="als-sum-check" />
      <span>{text}</span>
    </div>
  );
}

function SummarySection({ icon: Icon, title, items, tabKey, onEdit }) {
  return (
    <div className="als-sum-section">
      <div className="als-sum-sec-head">
        <div className="als-sum-sec-icon">
          <Icon size={16} />
        </div>
        <span className="als-sum-sec-title">{title}</span>
        <button
          type="button"
          className="als-sum-edit-link"
          onClick={() => onEdit(tabKey, { edit: true })}
        >
          Edit
        </button>
      </div>
      <div className="als-sum-items">
        {items.length === 0 ? (
          <div className="als-sum-empty">Not configured</div>
        ) : (
          items.map((t, i) => <SummaryItem key={i} text={t} />)
        )}
      </div>
    </div>
  );
}

function ConfigSummaryTab({ onEditTab }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const year = new Date().getFullYear();
        const [hrRes, shiftsRes, holidayRes] = await Promise.allSettled([
          API.get("/organisation/me/hr-settings"),
          fetchShiftTemplates({ page: 1, page_size: 5 }),
          fetchWfHolidays({ from_date: `${year}-01-01`, to_date: `${year}-12-31` }),
        ]);

        const hr = hrRes.status === "fulfilled" ? hrRes.value.data : {};
        const att = hr?.attendance_settings || hr?.hr_settings?.attendance || {};
        const profile = await fetchWfAttendanceProfile().catch(() => null);

        const method = resolveCurrentSource({}, att, profile || {});
        const shifts = shiftsRes.status === "fulfilled" ? shiftsRes.value : { items: [], total: 0 };
        const holidayRows = holidayRes.status === "fulfilled" ? holidayRes.value : [];
        const holidays = Array.isArray(holidayRows) && holidayRows.length
          ? { financial_year: year, total_holidays: holidayRows.length }
          : null;

        setData({ att, method, shifts, holidays });
      } catch (_) {
        setData({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="als-loading">
        <Loader2 size={22} className="als-spin" />
        Loading summary…
      </div>
    );
  }

  const { att = {}, method, shifts = {}, holidays } = data || {};

  // Attendance Method items
  const methodItems = method ? [METHOD_LABEL_MAP[method] || method] : [];

  // Shift Management items
  const shiftItems = (shifts.items || []).slice(0, 3).map(
    (s) => `${s.name} · ${fmt12(s.start_time)} – ${fmt12(s.end_time)}`
  );
  if ((shifts.total || 0) > 3) shiftItems.push(`+ ${shifts.total - 3} more shifts`);

  // Attendance Rules items
  const rulesItems = [];
  const graceMin = att.grace_minutes ?? att.late_coming_threshold_minutes;
  if (graceMin !== undefined) rulesItems.push(`Grace Time: ${graceMin} Minutes`);
  const halfDay = att.half_day_hours ?? (att.half_day_threshold_minutes ? att.half_day_threshold_minutes / 60 : null);
  if (halfDay !== null && halfDay !== undefined) rulesItems.push(`Half Day: ${halfDay} Hours`);
  const fullDay = att.full_day_hours;
  if (fullDay !== undefined) rulesItems.push(`Full Day: ${fullDay} Hours`);
  const ot = att.overtime_rule;
  if (ot) rulesItems.push(`Overtime: ${OT_RULE_LABEL[ot] || ot}`);
  if (rulesItems.length === 0) rulesItems.push("Grace Time: 15 Minutes", "Half Day: 4 Hours");

  // Weekly Off & Monthly Off items
  const offSummary = weeklyOffSummary(att);
  const woItems = offSummary.weekly.length ? offSummary.weekly : ["No weekly off"];
  const moItems = offSummary.monthly.length
    ? offSummary.monthly.map((m) => `${m} of every month`)
    : ["No monthly off"];

  // Holiday Calendar items
  const holidayItems = [];
  if (holidays?.financial_year) holidayItems.push(`FY ${holidays.financial_year}`);
  if (holidays?.total_holidays !== undefined) holidayItems.push(`${holidays.total_holidays} Holidays`);
  if (holidayItems.length === 0) holidayItems.push("Not configured");

  return (
    <div className="als-tab-body">
      <div className="als-section-header">
        <div className="als-section-icon">
          <LayoutList size={18} />
        </div>
        <div>
          <h2 className="als-section-title">Attendance Configuration Summary</h2>
          <p className="als-section-sub">Overview of your current attendance & leave configuration.</p>
        </div>
      </div>

      <div className="als-summary-card">
        <SummarySection
          icon={CheckCircle2}
          title="Attendance Method"
          items={methodItems}
          tabKey="method"
          onEdit={onEditTab}
        />
        <SummarySection
          icon={Clock}
          title="Shift Management"
          items={shiftItems}
          tabKey="shifts"
          onEdit={onEditTab}
        />
        <SummarySection
          icon={Settings2}
          title="Attendance Rules"
          items={rulesItems}
          tabKey="rules"
          onEdit={onEditTab}
        />
        <SummarySection
          icon={CalendarOff}
          title="Weekly Off"
          items={woItems}
          tabKey="weekly-off"
          onEdit={onEditTab}
        />
        <SummarySection
          icon={CalendarOff}
          title="Monthly Off"
          items={moItems}
          tabKey="weekly-off"
          onEdit={onEditTab}
        />
        <SummarySection
          icon={CalendarCheck}
          title="Holiday Calendar"
          items={holidayItems}
          tabKey="holiday"
          onEdit={() => {
            window.location.href = "/holiday-calendar";
          }}
        />

        <div className="als-sum-divider" />

        <div className="als-sum-footer">
          <button
            type="button"
            className="als-btn-primary als-btn-full"
            onClick={() => onEditTab("method", { edit: true })}
          >
            <Edit3 size={15} />
            Edit Attendance Settings
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function AttendanceLeaveSetupPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const [status, setStatus] = useState({ flags: {}, att: {}, source: "", shiftCount: 0 });
  const [statusLoading, setStatusLoading] = useState(true);
  const [unlockedTabs, setUnlockedTabs] = useState(() => new Set());

  const tabParam = searchParams.get("tab");
  const editRequested = searchParams.get("edit") === "1";
  const returnTo = searchParams.get("returnTo");
  const autoCreateShift = searchParams.get("create") === "1";
  const flags = status.flags || {};
  const allComplete = isAlsSetupComplete(flags);
  // The former Monthly Off step is part of the combined Weekly Off & Monthly Off step.
  const activeTab = (tabParam === "monthly-off" ? "weekly-off" : tabParam) || (allComplete ? "summary" : "method");
  const stepComplete = Boolean(SETUP_TAB_KEYS.includes(activeTab) && flags[activeTab]);
  const isEditing = editRequested || unlockedTabs.has(activeTab);
  // The weekly/monthly off calendar already shows the saved state, so it opens directly.
  const showCompletedPanel = stepComplete && !isEditing && activeTab !== "weekly-off";

  const setTab = useCallback(
    (key, options = {}) => {
      const next = { tab: key };
      if (options.edit) next.edit = "1";
      if (returnTo) next.returnTo = returnTo;
      if (options.create) next.create = "1";
      setUnlockedTabs(options.edit ? new Set([key]) : new Set());
      setSearchParams(next, { replace: true });
    },
    [returnTo, setSearchParams]
  );

  const refreshStatus = useCallback(async () => {
    const next = await loadAlsStatus();
    setStatus(next);
    return next;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await loadAlsStatus();
        if (!cancelled) setStatus(next);
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (statusLoading) return;
    if (!allComplete) return;
    if (tabParam) return;
    setSearchParams({ tab: "summary" }, { replace: true });
  }, [statusLoading, allComplete, tabParam, setSearchParams]);

  const handleConfigured = async () => {
    const next = await refreshStatus();
    const nowComplete = isAlsSetupComplete(next.flags);
    if (nowComplete && searchParams.get("edit") !== "1") {
      toast.success("Attendance setup is complete.");
      setUnlockedTabs(new Set());
    }
    // Opened from Attendance module with returnTo — honor that exit path.
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    // Settings hub flow — always return to All Settings after a successful save.
    navigateToSettingsHome(navigate, { replace: true });
  };

  const unlockCurrentTab = () => {
    setUnlockedTabs(new Set([activeTab]));
    setSearchParams({ tab: activeTab, edit: "1" }, { replace: true });
  };

  if (statusLoading) {
    return (
      <div className="als-root">
        <div className="als-loading">
          <Loader2 className="als-spin" size={22} />
          Loading attendance setup…
        </div>
      </div>
    );
  }

  return (
    <div className="als-root">
      <div className="als-page-header">
        <h1 className="als-page-title">Attendance Setup</h1>
        <p className="als-page-sub">
          {allComplete
            ? "Attendance setup is complete. Review the summary below, or open a step to edit it."
            : "Configure attendance methods, shifts, rules, and weekly & monthly off days."}
        </p>
      </div>

      <div className="als-content">
        {showCompletedPanel ? (
          <StepCompletedPanel
            tabKey={activeTab}
            currentValue={formatAlsCurrentValue(activeTab, status)}
            onEdit={unlockCurrentTab}
            showSummaryLink={allComplete}
            onViewSummary={() => setTab("summary")}
          />
        ) : (
          <>
            {stepComplete && isEditing && activeTab !== "weekly-off" ? (
              <div className="als-editing-bar">
                <Info size={14} />
                <span>You are editing a completed page. Save your changes when finished.</span>
                {allComplete ? (
                  <button type="button" className="als-editing-link" onClick={() => setTab("summary")}>
                    Back to summary
                  </button>
                ) : null}
              </div>
            ) : null}
            {activeTab === "method" && <AttendanceMethodTab onConfigured={handleConfigured} />}
            {activeTab === "shifts" && (
              <ShiftManagementTab
                onConfigured={handleConfigured}
                returnTo={returnTo}
                autoCreate={autoCreateShift}
              />
            )}
            {activeTab === "rules" && <AttendanceRulesTab onConfigured={handleConfigured} />}
            {activeTab === "weekly-off" && (
              <WeeklyMonthlyOffTab
                onConfigured={handleConfigured}
                onCancel={() =>
                  returnTo ? navigate(returnTo, { replace: true }) : navigateToSettingsHome(navigate, { replace: true })
                }
              />
            )}
            {activeTab === "summary" && <ConfigSummaryTab onEditTab={setTab} />}
          </>
        )}
      </div>
    </div>
  );
}
